import { FastField, Field } from 'formik';
import { fieldToTextField } from 'formik-mui';
import { Hidden } from '../Hidden';
import { GoogleSearchBase } from './GoogleSearchBase';
import { useGoogleMaps } from './useGoogleMaps.hook';
import type { GoogleSearchBaseProps } from './GoogleSearchBase';
import type { TextFieldProps } from '@mui/material';

export interface GoogleSearchProps {
  readonly useFastField?: boolean;
  readonly googlePlaceIdName?: string;
  readonly includeLatLngField?: boolean;
  readonly updateAsyncFields?: string[];
  readonly onSearchSelect?: GoogleSearchBaseProps['onSearchSelect'];
  readonly label?: React.ReactNode;
  readonly loadGoogleScriptExternally?: boolean;
}

export const GoogleSearch: React.FC<GoogleSearchProps & TextFieldProps> = ({
  id, name,
  useFastField = true,
  googlePlaceIdName = 'googlePlaceId',
  includeLatLngField = false,
  updateAsyncFields = [],
  onSearchSelect = () => {},
  label = null,
  helperText = null,
  loadGoogleScriptExternally = false,
  ...restProps
}) => {
  useGoogleMaps(loadGoogleScriptExternally);
  const FieldComponent = useFastField ? FastField : Field;

  return (
    <>
      <FieldComponent
        name={name}
      >
        {(props) => {
          const hasError = props.form.errors && props.form.errors[name];
          const isTouched = props.form.touched && props.form.touched[name];

          const displayHelperText = (hasError && isTouched) || Boolean(helperText);

          return (
            <GoogleSearchBase
              onSearchSelect={onSearchSelect}
              setFieldValue={props.form.setFieldValue}
              updateAsyncFields={updateAsyncFields}
              includeLatLngField={includeLatLngField}
              googlePlaceIdName={googlePlaceIdName}
              id={id || name}
              label={label}
              {...fieldToTextField(props)}
              {...restProps}
              helperText={displayHelperText ? (
                <>
                  {hasError && isTouched ? props.form.errors[name] : helperText}
                </>
              ) : null}
            />
          );
        }}
      </FieldComponent>

      <Hidden name={googlePlaceIdName} />

      {includeLatLngField && (
        <Hidden name="latLng" />
      )}
    </>
  );
};
